import { apiClient } from '../api/apiClient';
import { authService } from '../auth/authService';

export interface AppointmentTypeInfo {
  value: string;
  display_name: string;
  is_online_consultation: boolean;
}

class AppointmentTypesService {
  private cachedTypes: AppointmentTypeInfo[] | null = null;
  private fetchPromise: Promise<AppointmentTypeInfo[]> | null = null;

  /**
   * Fetch appointment types from backend API.
   * Results are cached after first fetch.
   * Skips the API call when not authenticated (no JWT) and returns fallbacks to avoid 401.
   */
  async getAppointmentTypes(forceRefresh: boolean = false): Promise<AppointmentTypeInfo[]> {
    if (!forceRefresh && this.cachedTypes) {
      return this.cachedTypes;
    }

    const token = authService.getToken();
    if (!token) {
      return this.getFallbackTypes();
    }

    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = apiClient
      .get<AppointmentTypeInfo[]>('/api/v1/appointment-types/', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        this.cachedTypes = response.data;
        this.fetchPromise = null;
        return response.data;
      })
      .catch((error) => {
        this.fetchPromise = null;
        console.error('Failed to fetch appointment types:', error);
        // Return fallback values if API fails
        return this.getFallbackTypes();
      });

    return this.fetchPromise;
  }

  /**
   * Get a specific appointment type by value
   */
  async getAppointmentType(value: string): Promise<AppointmentTypeInfo | null> {
    const types = await this.getAppointmentTypes();
    return types.find((t) => t.value === value) || null;
  }

  /**
   * Check if an appointment type is online consultation
   */
  async isOnlineConsultationType(value: string): Promise<boolean> {
    const type = await this.getAppointmentType(value);
    return type?.is_online_consultation ?? false;
  }

  /**
   * Get display name for appointment type
   */
  async getDisplayName(value: string): Promise<string> {
    const type = await this.getAppointmentType(value);
    return type?.display_name ?? 'Hospital Visit';
  }

  /**
   * Fallback values if API is unavailable
   * These should match the backend enum
   */
  private getFallbackTypes(): AppointmentTypeInfo[] {
    return [
      {
        value: 'online_consultation',
        display_name: 'Online Consultation',
        is_online_consultation: true,
      },
      {
        value: 'hospital_visit',
        display_name: 'Hospital Visit',
        is_online_consultation: false,
      },
      {
        value: 'consultation',
        display_name: 'Online Consultation',
        is_online_consultation: true,
      },
      {
        value: 'follow_up',
        display_name: 'Follow-up',
        is_online_consultation: false,
      },
      {
        value: 'emergency',
        display_name: 'Emergency',
        is_online_consultation: false,
      },
    ];
  }

  /**
   * Clear cache (useful for testing or after updates)
   */
  clearCache(): void {
    this.cachedTypes = null;
    this.fetchPromise = null;
  }
}

export const appointmentTypesService = new AppointmentTypesService();
